/**
 * Power-ups — all eight (Design Bible §9.1, FS-07). One stored, one active; picking up while stored
 * is full swaps the stored one. Effects compose through the modifier input and the hit chain, never
 * by patching constants.
 */
import type { EventBus } from '../core/events';
import type { EventMap, PowerUpName } from '../core/event-map';

/**
 * How a power-up spends itself, which is also how its slot reads (FS-12 §4.2 elements 5/6):
 * `timed` drains a clock, `charge` drains a count, `instant` resolves on activation.
 */
export type PowerUpSpend = 'timed' | 'charge' | 'instant';

export interface PowerUpDef {
  readonly name: PowerUpName;
  /**
   * The one sentence FS-01 R42 requires, living beside the mechanic's definition rather than in the
   * UI — the How to Play screen generates its card from this, so a power-up cannot be added
   * without its explanation (FS-12 R35).
   */
  readonly oneSentence: string;
  /** Seconds for `timed`; charges for `charge`; 0 for `instant`. */
  readonly amount: number;
  readonly spend: PowerUpSpend;
  /** The slot glyph (FS-07 R28 — a distinct shape per kind, so the slot reads in greyscale). */
  readonly glyph: string;
}

/** All eight (Design Bible §9.1). The registry is the source; the lists below are derived from it. */
export const POWERUPS: { readonly [K in PowerUpName]: PowerUpDef } = {
  'Phase Shift': {
    name: 'Phase Shift',
    oneSentence: 'You pass straight through hazards for three seconds.',
    amount: 3.0,
    spend: 'timed',
    glyph: '◇',
  },
  'Harmonic Shield': {
    name: 'Harmonic Shield',
    oneSentence: 'Absorbs the next hit you take, then it is spent.',
    amount: 0,
    spend: 'instant',
    glyph: '⬡',
  },
  'Tempo Brake': {
    name: 'Tempo Brake',
    oneSentence: 'Halves the speed the tunnel comes at you for four seconds. The music keeps its tempo.',
    amount: 4.0,
    spend: 'timed',
    glyph: '⏸',
  },
  'Overdrive Core': {
    name: 'Overdrive Core',
    oneSentence: 'Fills the Resonance meter, which starts Overdrive immediately.',
    amount: 0,
    spend: 'instant',
    glyph: '◉',
  },
  Wavebreaker: {
    name: 'Wavebreaker',
    oneSentence: 'Dissolves every hazard already on screen and clears the next five seconds.',
    amount: 5.0,
    spend: 'timed',
    glyph: '✖',
  },
  'Echo Pilot': {
    name: 'Echo Pilot',
    oneSentence: 'A ghost flies the safe line ahead of you for six seconds. Follow it.',
    amount: 6.0,
    spend: 'timed',
    glyph: '⟡',
  },
  'Prism Split': {
    name: 'Prism Split',
    oneSentence: 'Two decoys fly with you; each one absorbs a hit before you do.',
    amount: 2,
    spend: 'charge',
    glyph: '⋈',
  },
  'Null Field': {
    name: 'Null Field',
    oneSentence: 'Flattens the next four hazards to a quarter of their height. The safe gap is unchanged.',
    amount: 4,
    spend: 'charge',
    glyph: '▭',
  },
};

export const ALL_POWERUPS: readonly PowerUpName[] = Object.keys(POWERUPS) as PowerUpName[];

/** Everything the drop pool may hand out — every power-up, in the registry's order. */
export const GRANTABLE: PowerUpName[] = [...ALL_POWERUPS];

function timedDuration(name: PowerUpName): number {
  const def = POWERUPS[name];
  return def.spend === 'timed' ? def.amount : 0;
}

const NULL_FIELD_HAZARDS = POWERUPS['Null Field'].amount;
const PRISM_DECOYS = POWERUPS['Prism Split'].amount;
const NULL_FIELD_SCALE = 0.25;

export interface PowerUpHooks {
  fillResonance(): void;
  wavebreaker(): void;
}

export class PowerUps {
  stored: PowerUpName | null = null;
  active: PowerUpName | null = null;
  activeTimer = 0;
  shieldReady = false;
  decoys = 0; // Prism Split
  nullFieldRemaining = 0; // Null Field

  constructor(
    private bus: EventBus<EventMap>,
    private hooks: PowerUpHooks,
  ) {}

  reset(): void {
    this.stored = null;
    this.active = null;
    this.activeTimer = 0;
    this.shieldReady = false;
    this.decoys = 0;
    this.nullFieldRemaining = 0;
  }

  grant(name: PowerUpName): void {
    this.stored = name;
    this.bus.emit('powerup:stored', { powerup: name });
  }

  use(): void {
    if (!this.stored) return;
    const name = this.stored;
    this.stored = null;

    switch (name) {
      case 'Overdrive Core':
        this.hooks.fillResonance();
        break;
      case 'Harmonic Shield':
        this.shieldReady = true;
        break;
      case 'Wavebreaker':
        this.hooks.wavebreaker();
        this.startActive(name);
        break;
      case 'Prism Split':
        this.decoys = PRISM_DECOYS;
        this.startActive(name);
        break;
      case 'Null Field':
        this.nullFieldRemaining = NULL_FIELD_HAZARDS;
        this.active = name;
        this.activeTimer = 0; // count-based, not timed
        break;
      default:
        // Phase Shift, Tempo Brake, Echo Pilot — timed.
        this.startActive(name);
        break;
    }
    this.bus.emit('powerup:activated', { powerup: name });
  }

  private startActive(name: PowerUpName): void {
    this.active = name;
    this.activeTimer = timedDuration(name);
  }

  /** Harmonic Shield absorb (before the Prism decoy in the chain). */
  absorbHit(): boolean {
    if (this.shieldReady) {
      this.shieldReady = false;
      return true;
    }
    return false;
  }

  /** Prism decoy absorb. */
  absorbDecoy(): boolean {
    if (this.decoys > 0) {
      this.decoys -= 1;
      if (this.decoys === 0 && this.active === 'Prism Split') this.expire();
      return true;
    }
    return false;
  }

  /** Flatten a spawning hazard's amplitude (Null Field). Returns the amp scale to apply. */
  nullFieldScale(): number {
    if (this.nullFieldRemaining > 0) {
      this.nullFieldRemaining -= 1;
      if (this.nullFieldRemaining === 0 && this.active === 'Null Field') this.expire();
      return NULL_FIELD_SCALE;
    }
    return 1.0;
  }

  get intangible(): boolean {
    return this.active === 'Phase Shift';
  }
  get worldSpeedScale(): number {
    return this.active === 'Tempo Brake' ? 0.5 : 1.0;
  }
  get echoPilot(): boolean {
    return this.active === 'Echo Pilot';
  }

  private expire(): void {
    const expired = this.active;
    this.active = null;
    this.activeTimer = 0;
    if (expired) this.bus.emit('powerup:expired', { powerup: expired });
  }

  step(dt: number): void {
    if (this.active && this.activeTimer > 0) {
      this.activeTimer -= dt;
      if (this.activeTimer <= 0) {
        if (this.active === 'Prism Split') this.decoys = 0;
        this.expire();
      }
    }
  }
}
